import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Card.module.css';

export type CardTexture = 'plain' | 'dots' | 'glow' | 'dotsGlow' | 'entity' | 'highlight' | 'accent' | 'dashed' | 'create';

type Props = HTMLAttributes<HTMLDivElement> & {
  texture?: CardTexture;
  /** 'none' = no padding class: the caller's class sets it. */
  padding?: 22 | 20 | 24 | 8 | 0 | 'none';
  /** hover = translateY(-3px) + the accent shadow. */
  lift?: boolean;
  clip?: boolean;
  scrollX?: boolean;
  /** position:relative, for a Notch or an absolute decoration inside. */
  relative?: boolean;
  children?: ReactNode;
};

const PAD: Record<number, string | undefined> = { 22: s.p22, 20: s.p20, 24: s.p24, 8: s.p8, 0: s.pNone };

/** Card: dots, glow, dashed (create-new), highlight (accent border). Clickable when onClick is set. */
export function Card({ texture = 'plain', padding = 22, lift, clip, scrollX, relative, className, onClick, children, ...rest }: Props) {
  const clickable = !!onClick;
  const onKeyDown = clickable
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.currentTarget.click();
        }
      }
    : undefined;
  return (
    <div
      className={cx(
        s.card,
        padding !== 'none' && PAD[padding],
        texture !== 'plain' && s[texture],
        lift && s.lift,
        clickable && s.clickable,
        clip && s.clip,
        scrollX && s.scrollX,
        relative && s.relative,
        className,
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      {...rest}
    >
      {children}
    </div>
  );
}

/** The card's eyebrow (12px #85858B) and title (15px/500). */
export function CardHead({ eyebrow, title, className }: { eyebrow?: ReactNode; title: ReactNode; className?: string }) {
  return (
    <div className={cx(s.head, className)}>
      {eyebrow && <span className={s.eyebrow}>{eyebrow}</span>}
      <span className={s.title}>{title}</span>
    </div>
  );
}
